import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    TextField,
    Button,
    IconButton,
    Chip,
    CircularProgress,
    Card,
    CardContent,
    Tooltip,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormLabel,
    Autocomplete,
} from '@mui/material';
import {
    Add as AddIcon,
    Visibility as ViewIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    AttachMoney,
    People,
    CheckCircle,
    CloudDownload,
    PostAdd,
    CalendarMonth,
    Assessment as AssessmentIcon,
    Print as PrintIcon,
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import payrollsService from '../services/payrolls.service';
import { branchesService } from '../services/branches.service';
import {
    Payroll,
    PayrollDetail,
    ProcessPayrollRequest,
} from '../types/payrolls.types';
import { Branch } from '../types/branches.types';
import { toast } from 'react-toastify';
import config from '../config';
import { companyInfoService, CompanyInfo } from '../services/companyInfo.service';
import PayrollDetailsDialog from '../components/payrolls/PayrollDetailsDialog';
import BankFileExportDialog from '../components/payrolls/BankFileExportDialog';

const translations = {
    en: {
        title: 'Payroll Management',
        titleAr: 'إدارة الرواتب',
        payrollNumber: 'Payroll #',
        type: 'Type',
        branch: 'Branch',
        period: 'Period',
        month: 'Month',
        year: 'Year',
        basicSalary: 'Basic Salary',
        overtime: 'Overtime',
        allowances: 'Allowances',
        deductions: 'Deductions',
        netSalary: 'Net Salary',
        status: 'Status',
        actions: 'Actions',
        processPayroll: 'Process Payroll',
        viewDetails: 'View Details',
        exportBankFile: 'Export Bank File',
        post: 'Post',
        delete: 'Delete',
        refresh: 'Refresh',
        cancel: 'Cancel',
        save: 'Save',
        process: 'Process',
        selectType: 'Select Type',
        selectBranch: 'Select Branch',
        selectMonth: 'Select Month',
        selectYear: 'Year',
        management: 'Management',
        branchType: 'Branch',
        allTypes: 'All Types',
        loading: 'Loading...',
        noData: 'No payroll records',
        confirmDelete: 'Are you sure you want to delete this payroll?',
        processSuccess: 'Payroll processed successfully',
        postSuccess: 'Payroll posted successfully',
        deleteSuccess: 'Payroll deleted successfully',
        error: 'An error occurred',
        draft: 'Draft',
        processed: 'Processed',
        posted: 'Posted',
        cancelled: 'Cancelled',
        totalPayrolls: 'Total Payrolls',
        totalAmount: 'Total Amount',
        processedPayrolls: 'Processed',
        createJournal: 'Create Journal Entry',
        createJournalSuccess: 'Journal entry created successfully',
        selectBranches: 'Select Branches',
        allUnposted: 'All Unposted Branches',
        deleteJournal: 'Delete Journal Entry',
        deleteJournalSuccess: 'Journal entry deleted successfully',
        deleteJournalConfirm: 'Are you sure you want to delete this journal entry?',
        kwd: 'KWD',
        notes: 'Notes',
        january: 'January',
        february: 'February',
        march: 'March',
        april: 'April',
        may: 'May',
        june: 'June',
        july: 'July',
        august: 'August',
        september: 'September',
        october: 'October',
        november: 'November',
        december: 'December',
    },
    ar: {
        title: 'إدارة الرواتب',
        titleAr: 'إدارة الرواتب',
        payrollNumber: 'رقم الراتب #',
        type: 'النوع',
        branch: 'الفرع',
        period: 'الفترة',
        month: 'الشهر',
        year: 'السنة',
        basicSalary: 'الراتب الأساسي',
        overtime: 'الوقت الإضافي',
        allowances: 'البدلات',
        deductions: 'الاستقطاعات',
        netSalary: 'صافي الراتب',
        status: 'الحالة',
        actions: 'الإجراءات',
        processPayroll: 'معالجة الراتب',
        viewDetails: 'عرض التفاصيل',
        exportBankFile: 'تصدير ملف البنك',
        post: 'ترحيل',
        delete: 'حذف',
        refresh: 'تحديث',
        cancel: 'إلغاء',
        save: 'حفظ',
        process: 'معالجة',
        selectType: 'اختر النوع',
        selectBranch: 'اختر الفرع',
        selectMonth: 'اختر الشهر',
        selectYear: 'السنة',
        management: 'الإدارة',
        branchType: 'فرع',
        allTypes: 'جميع الأنواع',
        loading: 'جاري التحميل...',
        noData: 'لا توجد سجلات رواتب',
        confirmDelete: 'هل أنت متأكد من حذف هذا الراتب؟',
        processSuccess: 'تمت معالجة الراتب بنجاح',
        postSuccess: 'تم ترحيل الراتب بنجاح',
        deleteSuccess: 'تم حذف الراتب بنجاح',
        error: 'حدث خطأ',
        draft: 'مسودة',
        processed: 'معالج',
        posted: 'مرحّل',
        cancelled: 'ملغي',
        totalPayrolls: 'إجمالي الرواتب',
        totalAmount: 'الإجمالي',
        processedPayrolls: 'معالج',
        createJournal: 'إنشاء قيد محاسبي',
        createJournalSuccess: 'تم إنشاء القيد المحاسبي بنجاح',
        selectBranches: 'اختر الفروع',
        allUnposted: 'كل الفروع غير المرحّلة',
        deleteJournal: 'حذف القيد المحاسبي',
        deleteJournalSuccess: 'تم حذف القيد المحاسبي بنجاح',
        deleteJournalConfirm: 'هل أنت متأكد من حذف هذا القيد المحاسبي؟',
        kwd: 'د.ك',
        notes: 'ملاحظات',
        january: 'يناير',
        february: 'فبراير',
        march: 'مارس',
        april: 'أبريل',
        may: 'مايو',
        june: 'يونيو',
        july: 'يوليو',
        august: 'أغسطس',
        september: 'سبتمبر',
        october: 'أكتوبر',
        november: 'نوفمبر',
        december: 'ديسمبر',
    },
};

// Single source of truth for the payroll period (must match PayrollService.ValidatePayrollPeriod on the server).
const periodTypeFor = (type: string): '21-20' | 'full' => (type === 'Management' ? 'full' : '21-20');

const PayrollsPage: React.FC = () => {
    const { language } = useLanguage();
    const { mode } = useTheme();
    const t = translations[language];
    const isRTL = language === 'ar';
    const navigate = useNavigate();

    const userBranchIds = JSON.parse(localStorage.getItem('userBranchIds') || '[]');
    const canEditPayroll = (payroll: Payroll) => {
        if (payroll.type === 'Management') return userBranchIds.length === 0;
        return userBranchIds.length === 0 || (payroll.branchId && userBranchIds.includes(payroll.branchId));
    };
    const userRoles = (() => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return [] as string[];
        const payload = JSON.parse(atob(token.split('.')[1]));
        const roles = payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
        return roles ? (Array.isArray(roles) ? roles : [roles]) : [];
      } catch { return [] as string[]; }
    })();
    const canPost = (() => { try { const token = localStorage.getItem('token'); if (!token) return false; const payload = JSON.parse(atob(token.split('.')[1])); const perms = payload.Permission ? (Array.isArray(payload.Permission) ? payload.Permission : [payload.Permission]) : []; return perms.some((p: string) => p.endsWith('_POST')); } catch { return false; } })();

    const [payrolls, setPayrolls] = useState<Payroll[]>([]);
    const [branches, setBranches] = useState<Branch[]>([]);
    const [loading, setLoading] = useState(false);
    const [postingId, setPostingId] = useState<number | null>(null);
    const [processingPayroll, setProcessingPayroll] = useState(false);
    const [selectedType, setSelectedType] = useState<'Management' | 'Branch' | ''>('');
    const [processDialogOpen, setProcessDialogOpen] = useState(false);
    const [journalDialogOpen, setJournalDialogOpen] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(
        new Date().getMonth() + 1
    );
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
    const [bankFileDialogOpen, setBankFileDialogOpen] = useState(false);
    const [selectedPayroll, setSelectedPayroll] = useState<Payroll | null>(
        null
    );
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [journalBranchIds, setJournalBranchIds] = useState<number[]>([]);
    const [deleteJournalDialogOpen, setDeleteJournalDialogOpen] = useState(false);
    const [deleteJournalMonth, setDeleteJournalMonth] = useState(new Date().getMonth() + 1);
    const [deleteJournalYear, setDeleteJournalYear] = useState(new Date().getFullYear());
    const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

    const [processFormData, setProcessFormData] =
        useState<ProcessPayrollRequest>({
            type: 'Management',
            month: new Date().getMonth() + 1,
            year: new Date().getFullYear(),
            periodType: '21-20',
            notes: '',
        });

    const months = [
        { value: 1, label: t.january },
        { value: 2, label: t.february },
        { value: 3, label: t.march },
        { value: 4, label: t.april },
        { value: 5, label: t.may },
        { value: 6, label: t.june },
        { value: 7, label: t.july },
        { value: 8, label: t.august },
        { value: 9, label: t.september },
        { value: 10, label: t.october },
        { value: 11, label: t.november },
        { value: 12, label: t.december },
    ];

    useEffect(() => {
        loadBranches();
        loadPayrolls();
        companyInfoService.getCompanyInfo().then(setCompanyInfo).catch(() => {});
    }, []);

    useEffect(() => {
        loadPayrolls();
    }, [selectedType]);

    const loadBranches = async () => {
        try {
            const data = await branchesService.getAll();
            setBranches(data);
        } catch (error) {
            console.error('Failed to load branches:', error);
            toast.error(t.error);
        }
    };

    const loadPayrolls = async () => {
        setLoading(true);
        try {
            const data = await payrollsService.getAll(
                selectedType || undefined
            );
            const filteredData = data.filter((payroll: Payroll) => canEditPayroll(payroll));
            setPayrolls(filteredData);
        } catch (error) {
            console.error('Failed to load payrolls:', error);
            toast.error(t.error);
        } finally {
            setLoading(false);
        }
    };

    const [processBranchIds, setProcessBranchIds] = useState<number[]>([]);

    const handleOpenProcessDialog = () => {
        setProcessFormData({
            type: 'Management',
            month: new Date().getMonth() + 1,
            year: new Date().getFullYear(),
            notes: '',
        });
        setProcessBranchIds([]);
        setProcessDialogOpen(true);
    };

    const handleProcessPayroll = async () => {
        try {
            setProcessingPayroll(true);
            // 🔒 Period is fixed by payroll type (no manual choice): Management = full calendar month, Branch = 21→20.
            // The server enforces the same rule (PayrollService.ValidatePayrollPeriod).
            const periodType = periodTypeFor(processFormData.type);
            const month = processFormData.month;
            const year = processFormData.year;

            let fromDate: Date;
            let toDate: Date;
            let payrollMonth: number;
            let payrollYear: number;

            if (periodType === '21-20') {
                fromDate = new Date(year, month - 2, 21);
                toDate = new Date(year, month - 1, 20);
                payrollMonth = toDate.getMonth() + 1;
                payrollYear = toDate.getFullYear();
            } else {
                fromDate = new Date(year, month - 1, 1);
                toDate = new Date(year, month, 0);
                payrollMonth = toDate.getMonth() + 1;
                payrollYear = toDate.getFullYear();
            }

            // ✅ FIX: استخدام تنسيق محلي بدل toISOString لتجنّب bug الـ timezone
            // (الكويت GMT+3 يجعل toISOString يطرح يوماً عند الساعة 00:00 محلياً)
            const toLocalISO = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            const fromStr = toLocalISO(fromDate);
            const toStr = toLocalISO(toDate);

            if (processFormData.type === 'Branch' && processBranchIds.length > 0) {
                // Process each selected branch
                let success = 0;
                let failed = 0;
                for (const branchId of processBranchIds) {
                    try {
                        await payrollsService.process({
                            ...processFormData,
                            branchId,
                            month: payrollMonth,
                            year: payrollYear,
                            fromDate: fromStr,
                            toDate: toStr,
                        });
                        success++;
                    } catch {
                        failed++;
                    }
                }
                toast.success(isRTL ? `تم إنشاء ${success} كشف رواتب${failed > 0 ? ` (${failed} فشل)` : ''}` : `${success} payroll(s) created${failed > 0 ? ` (${failed} failed)` : ''}`);
            } else {
                await payrollsService.process({
                    ...processFormData,
                    month: payrollMonth,
                    year: payrollYear,
                    fromDate: fromStr,
                    toDate: toStr,
                });
                toast.success(t.processSuccess);
            }

            setProcessDialogOpen(false);
            loadPayrolls();
        } catch (error: any) {
            toast.error(error?.response?.data?.message || t.error);
        } finally {
            setProcessingPayroll(false);
        }
    };

    const handlePostPayroll = async (payrollId: number) => {
        try {
            setPostingId(payrollId);
            await payrollsService.post({ payrollId });
            toast.success(t.postSuccess);
            loadPayrolls();
        } catch (error) {
            console.error('Failed to post payroll:', error);
            toast.error(t.error);
        } finally {
            setPostingId(null);
        }
    };

    const handleCreateJournal = async () => {
        try {
            await payrollsService.createConsolidatedJournal(
                selectedMonth,
                selectedYear,
                journalBranchIds.length > 0 ? journalBranchIds : undefined
            );
            toast.success(t.createJournalSuccess);
            setJournalDialogOpen(false);
            setJournalBranchIds([]);
            loadPayrolls();
        } catch (error: any) {
            toast.error(error.response?.data?.message || t.error);
        }
    };

    const handleDeleteJournal = async () => {
        try {
            await payrollsService.deleteJournalEntry(deleteJournalMonth, deleteJournalYear);
            toast.success(t.deleteJournalSuccess);
            setDeleteJournalDialogOpen(false);
            loadPayrolls();
        } catch (error: any) {
            toast.error(error.response?.data?.message || t.error);
        }
    };

    const handleViewDetails = (payroll: Payroll) => {
        setSelectedPayroll(payroll);
        setDetailsDialogOpen(true);
    };

    const handleExportBankFile = (payroll: Payroll) => {
        setSelectedPayroll(payroll);
        setBankFileDialogOpen(true);
    };

    const handlePrintPayroll = async (payroll: Payroll) => {
        // ✅ جلب التقرير الكامل بتفاصيل الموظفين قبل الطباعة
        let reportData: any;
        try {
            reportData = await payrollsService.getReport(payroll.payrollId);
        } catch (e) {
            toast.error(isRTL ? 'فشل جلب تفاصيل الكشف' : 'Failed to load payroll details');
            return;
        }

        const fmt = (n: number) => (n || 0).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
        const fmtH = (n: number) => (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const branchLine = reportData.type === 'Branch'
            ? (isRTL ? reportData.branchNameAr || reportData.branchName : reportData.branchName)
            : (isRTL ? 'إدارة' : 'Management');
        const periodStr = `${new Date(reportData.periodStart).toLocaleDateString('en-GB')} → ${new Date(reportData.periodEnd).toLocaleDateString('en-GB')}`;
        const employees = reportData.employees || [];
        const totalBankAll = employees.reduce((s: number, e: any) => s + (e.bankAmount || 0), 0);
        const totalCashAll = employees.reduce((s: number, e: any) => s + (e.cashAmount || 0), 0);
        const fEarnings = employees.reduce((s: number, e: any) => s + (e.totalEarnings || 0), 0);
        const fDeductions = employees.reduce((s: number, e: any) => s + (e.totalDeductions || 0), 0);
        const fNet = employees.reduce((s: number, e: any) => s + (e.netSalary || 0), 0);
        const statusLabel = getStatusLabel(payroll.status);

        const renderRow = (emp: any, idx: number) => {
            const allowances = (emp.housingAllowance || 0) + (emp.transportAllowance || 0) + (emp.otherAllowances || 0);
            const dedAbs = emp.absencesAmount || emp.absences || 0;
            const dedPen = emp.penaltiesAmount || emp.penalties || 0;
            const dedOther = emp.otherDeductions || 0;
            const totalDed = dedAbs + dedPen + dedOther;
            return `
            <tr class="emp-row ${idx % 2 === 0 ? 'even' : 'odd'}">
              <td class="cell-code"><strong>${emp.employeeCode}</strong></td>
              <td class="cell-name">${isRTL ? (emp.employeeNameAr || emp.employeeName) : emp.employeeName}</td>
              <td class="cell-num">${fmtH(emp.totalRegularHours || 0)}</td>
              <td class="cell-num">${fmtH(emp.totalOvertimeHours || 0)}</td>
              <td class="cell-num">${fmtH(emp.offDayHours || 0)}</td>
              <td class="cell-num">${fmtH(emp.publicHolidayHours || 0)}</td>
              <td class="cell-amt basic">${fmt(emp.basicSalary || 0)}</td>
              <td class="cell-amt allow">${fmt(allowances)}</td>
              <td class="cell-amt ot">${fmt((emp.overtimePay || 0) + (emp.publicHolidayPay || 0) + (emp.offDayPay || 0))}</td>
              <td class="cell-amt earn"><strong>${fmt(emp.totalEarnings || 0)}</strong></td>
              <td class="cell-amt ded">${fmt(totalDed)}</td>
              <td class="cell-amt net"><strong>${fmt(emp.netSalary || 0)}</strong></td>
              <td class="cell-amt bank">${fmt(emp.bankAmount || 0)}</td>
              <td class="cell-amt cash">${fmt(emp.cashAmount || 0)}</td>
            </tr>`;
        };

        // ✅ تقسيم الموظفين إلى صفحات مع تكرار الـ Header
        const ROWS_FIRST_PAGE = 14;
        const ROWS_OTHER_PAGES = 18;
        const pages: any[][] = [];
        let idx = 0;
        while (idx < employees.length) {
            const size = pages.length === 0 ? ROWS_FIRST_PAGE : ROWS_OTHER_PAGES;
            pages.push(employees.slice(idx, idx + size));
            idx += size;
        }
        if (pages.length === 0) pages.push([]);

        const styles = `
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; padding: 18px; direction: ${isRTL ? 'rtl' : 'ltr'}; color: #1a1a1a; background: #fff; }
          .header { text-align: center; border-bottom: 3px solid #00796B; padding-bottom: 14px; margin-bottom: 18px; }
          .header img { max-height: 60px; margin-bottom: 6px; }
          .header h2 { color: #00796B; margin: 4px 0; font-size: 20px; }
          .header .subtitle { font-size: 11px; color: #555; }
          table.title-bar { width: 100%; border-collapse: collapse; background: linear-gradient(135deg, #00796B 0%, #00ACC1 100%); color: #fff; border-radius: 8px; margin-bottom: 14px; overflow: hidden; }
          table.title-bar td { padding: 12px 20px; vertical-align: middle; }
          table.title-bar h3 { font-size: 18px; margin: 0; }
          table.title-bar .meta { font-size: 12px; opacity: 0.95; }
          table.stats-tbl { width: 100%; border-collapse: separate; border-spacing: 6px 0; margin-bottom: 14px; }
          table.stats-tbl td { width: 20%; padding: 0; vertical-align: top; }
          .stat-card { padding: 10px 12px; border-radius: 8px; color: #fff; text-align: center; box-shadow: 0 2px 6px rgba(0,0,0,0.08); }
          .stat-card .label { font-size: 10px; opacity: 0.95; margin-bottom: 4px; }
          .stat-card .val { font-size: 16px; font-weight: bold; }
          .s-emp { background: linear-gradient(135deg, #6A1B9A, #AB47BC); }
          .s-earn { background: linear-gradient(135deg, #00796B, #26A69A); }
          .s-ded { background: linear-gradient(135deg, #C62828, #EF5350); }
          .s-net { background: linear-gradient(135deg, #1565C0, #42A5F5); }
          .s-bank { background: linear-gradient(135deg, #EF6C00, #FFA726); }
          .status-badge { display: inline-block; padding: 2px 10px; border-radius: 10px; font-size: 11px; font-weight: bold; background: rgba(255,255,255,0.25); }
          table.main { width: 100%; border-collapse: collapse; font-size: 10px; }
          table.main thead th { background: #00796B; color: #fff; padding: 8px 5px; text-align: center; font-weight: 600; border: 1px solid #00564E; font-size: 10px; }
          table.main thead th.group-hours { background: #5E35B1; }
          table.main thead th.group-earn { background: #00796B; }
          table.main thead th.group-ded { background: #C62828; }
          table.main thead th.group-net { background: #1565C0; }
          table.main tbody td { padding: 6px 5px; border: 1px solid #DDD; text-align: center; }
          table.main tbody tr.odd td { background: #FAFAFA; }
          table.main tbody tr.even td { background: #fff; }
          .cell-code { color: #00796B; font-weight: bold; text-align: ${isRTL ? 'right' : 'left'} !important; padding-${isRTL ? 'right' : 'left'}: 8px !important; }
          .cell-name { text-align: ${isRTL ? 'right' : 'left'} !important; padding-${isRTL ? 'right' : 'left'}: 8px !important; }
          .cell-num { color: #5E35B1; font-weight: 600; }
          .cell-amt { text-align: ${isRTL ? 'left' : 'right'} !important; padding-${isRTL ? 'left' : 'right'}: 8px !important; font-variant-numeric: tabular-nums; }
          .cell-amt.basic { color: #00796B; }
          .cell-amt.allow { color: #00897B; }
          .cell-amt.ot { color: #5E35B1; }
          .cell-amt.earn { color: #00695C; background: #E8F5E9 !important; }
          .cell-amt.ded { color: #C62828; }
          .cell-amt.net { color: #1565C0; background: #E3F2FD !important; font-size: 11px; }
          .cell-amt.bank { color: #EF6C00; }
          .cell-amt.cash { color: #F57C00; }
          tfoot tr td { background: linear-gradient(135deg, #00796B, #00ACC1) !important; color: #fff !important; font-weight: bold; padding: 10px 5px !important; font-size: 11px !important; border: 1px solid #00564E !important; }
          tfoot tr td.cell-amt { text-align: ${isRTL ? 'left' : 'right'} !important; }
          .footer { margin-top: 28px; display: flex; justify-content: space-between; gap: 30px; }
          .sig { text-align: center; flex: 1; }
          .sig .line { border-top: 1.5px solid #333; margin-top: 50px; padding-top: 6px; font-weight: bold; font-size: 11px; color: #333; }
          .footer-meta { margin-top: 14px; text-align: center; font-size: 9px; color: #999; }
          .page-break { page-break-before: always; break-before: page; height: 0; }
          @media print {
            @page { size: A4 landscape; margin: 10mm; }
            body { padding: 0; }
            table.main tbody tr { page-break-inside: avoid; }
            .footer { page-break-inside: avoid; }
          }
        `;

        const renderPage = (pageEmps: any[], pageIdx: number) => {
            const isLast = pageIdx === pages.length - 1;
            const headerBlock = `
              ${companyInfo ? `
              <div class="header">
                ${companyInfo.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" onerror="this.style.display='none'" />` : ''}
                <h2>${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</h2>
                <div class="subtitle">
                  ${companyInfo.address ? companyInfo.address + ' | ' : ''}${companyInfo.phone ? '📞 ' + companyInfo.phone + ' | ' : ''}${companyInfo.email ? '✉️ ' + companyInfo.email : ''}${companyInfo.website ? ' | 🌐 ' + companyInfo.website : ''}
                </div>
              </div>
              ` : ''}
              <table class="title-bar"><tr>
                <td style="text-align:${isRTL ? 'right' : 'left'};"><h3>${isRTL ? '📊 كشف رواتب' : '📊 Payroll'} - ${reportData.payrollNumber}</h3></td>
                <td style="text-align:${isRTL ? 'left' : 'right'};"><div class="meta"><strong>${branchLine}</strong> &nbsp;|&nbsp; <span class="status-badge">${statusLabel}</span> &nbsp;|&nbsp; ${isRTL ? 'الفترة' : 'Period'}: ${periodStr}${pages.length > 1 ? ` &nbsp;|&nbsp; <span class="status-badge">${isRTL ? `صفحة ${pageIdx + 1} من ${pages.length}` : `Page ${pageIdx + 1} of ${pages.length}`}</span>` : ''}</div></td>
              </tr></table>
              <table class="stats-tbl"><tr>
                <td><div class="stat-card s-emp"><div class="label">${isRTL ? 'الموظفين' : 'Employees'}</div><div class="val">${employees.length}</div></div></td>
                <td><div class="stat-card s-earn"><div class="label">${isRTL ? 'إجمالي المستحقات' : 'Total Earnings'}</div><div class="val">${fmt(fEarnings)}</div></div></td>
                <td><div class="stat-card s-ded"><div class="label">${isRTL ? 'إجمالي الخصومات' : 'Total Deductions'}</div><div class="val">${fmt(fDeductions)}</div></div></td>
                <td><div class="stat-card s-net"><div class="label">${isRTL ? 'صافي الرواتب' : 'Net Salary'}</div><div class="val">${fmt(fNet)}</div></div></td>
                <td><div class="stat-card s-bank"><div class="label">${isRTL ? 'بنك / نقدي' : 'Bank / Cash'}</div><div class="val" style="font-size:12px;">${fmt(totalBankAll)} / ${fmt(totalCashAll)}</div></div></td>
              </tr></table>
            `;
            const colHeader = `
              <thead>
                <tr>
                  <th rowspan="2">${isRTL ? 'الكود' : 'Code'}</th>
                  <th rowspan="2">${isRTL ? 'الموظف' : 'Employee'}</th>
                  <th colspan="4" class="group-hours">${isRTL ? 'الساعات' : 'Hours'}</th>
                  <th colspan="4" class="group-earn">${isRTL ? 'المستحقات (KWD)' : 'Earnings (KWD)'}</th>
                  <th class="group-ded">${isRTL ? 'الخصومات' : 'Deductions'}</th>
                  <th class="group-net">${isRTL ? 'الصافي' : 'Net'}</th>
                  <th colspan="2">${isRTL ? 'الدفع' : 'Payment'}</th>
                </tr>
                <tr>
                  <th class="group-hours">${isRTL ? 'عادي' : 'Reg'}</th>
                  <th class="group-hours">${isRTL ? 'إضافي' : 'OT'}</th>
                  <th class="group-hours">${isRTL ? 'راحة' : 'Off'}</th>
                  <th class="group-hours">${isRTL ? 'عطلة' : 'Hol'}</th>
                  <th>${isRTL ? 'أساسي' : 'Basic'}</th>
                  <th>${isRTL ? 'بدلات' : 'Allow'}</th>
                  <th>${isRTL ? 'OT+راحة+عطلة' : 'OT+Off+Hol'}</th>
                  <th>${isRTL ? 'الإجمالي' : 'Total'}</th>
                  <th class="group-ded">${isRTL ? 'الإجمالي' : 'Total'}</th>
                  <th class="group-net">${isRTL ? 'KWD' : 'KWD'}</th>
                  <th>${isRTL ? 'بنك' : 'Bank'}</th>
                  <th>${isRTL ? 'نقدي' : 'Cash'}</th>
                </tr>
              </thead>
            `;
            const bodyRows = pageEmps.map((emp: any, i: number) => renderRow(emp, i)).join('');
            const footerRow = isLast ? `
              <tfoot>
                <tr>
                  <td colspan="6" style="text-align:${isRTL ? 'right' : 'left'};padding-${isRTL ? 'right' : 'left'}:12px !important;">${isRTL ? `الإجمالي (${employees.length} موظف)` : `TOTALS (${employees.length} employees)`}</td>
                  <td class="cell-amt">${fmt(employees.reduce((s: number, e: any) => s + (e.basicSalary || 0), 0))}</td>
                  <td class="cell-amt">${fmt(employees.reduce((s: number, e: any) => s + (e.housingAllowance || 0) + (e.transportAllowance || 0) + (e.otherAllowances || 0), 0))}</td>
                  <td class="cell-amt">${fmt(employees.reduce((s: number, e: any) => s + (e.overtimePay || 0) + (e.publicHolidayPay || 0) + (e.offDayPay || 0), 0))}</td>
                  <td class="cell-amt">${fmt(fEarnings)}</td>
                  <td class="cell-amt">${fmt(fDeductions)}</td>
                  <td class="cell-amt">${fmt(fNet)}</td>
                  <td class="cell-amt">${fmt(totalBankAll)}</td>
                  <td class="cell-amt">${fmt(totalCashAll)}</td>
                </tr>
              </tfoot>
            ` : '';
            const signaturesBlock = isLast ? `
              <div class="footer">
                <div class="sig"><div class="line">${isRTL ? 'المحاسب' : 'Accountant'}</div></div>
                <div class="sig"><div class="line">${isRTL ? 'المدير المالي' : 'Financial Manager'}</div></div>
                <div class="sig"><div class="line">${isRTL ? 'المدير العام' : 'General Manager'}</div></div>
              </div>
              <div class="footer-meta">
                ${isRTL ? 'تم الإنشاء في' : 'Generated'}: ${new Date().toLocaleString(isRTL ? 'ar-KW' : 'en-GB')} &nbsp;|&nbsp; ${reportData.payrollNumber}
              </div>
            ` : '';
            const pageBreak = pageIdx > 0 ? '<div class="page-break"></div>' : '';
            return `${pageBreak}<div class="print-page">${headerBlock}<table class="main">${colHeader}<tbody>${bodyRows}</tbody>${footerRow}</table>${signaturesBlock}</div>`;
        };

        const html = `<!DOCTYPE html><html dir="${isRTL ? 'rtl' : 'ltr'}"><head><meta charset="UTF-8"><title>${reportData.payrollNumber} - ${branchLine}</title><style>${styles}</style></head><body>${pages.map((emps, i) => renderPage(emps, i)).join('')}</body></html>`;

        const w = window.open('', '_blank', 'width=1100,height=800');
        if (w) {
            w.document.write(html);
            w.document.close();
            w.focus();
            setTimeout(() => { w.print(); }, 600);
        }
    };

    const handleDelete = async () => {
        if (deletingId) {
            try {
                await payrollsService.delete(deletingId);
                toast.success(t.deleteSuccess);
                setDeleteConfirmOpen(false);
                setDeletingId(null);
                loadPayrolls();
            } catch (error) {
                console.error('Failed to delete payroll:', error);
                toast.error(t.error);
            }
        }
    };

    const handleOpenDeleteConfirm = (id: number) => {
        setDeletingId(id);
        setDeleteConfirmOpen(true);
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Draft':
                return 'default';
            case 'Processed':
                return 'info';
            case 'Posted':
                return 'success';
            case 'Cancelled':
                return 'error';
            default:
                return 'default';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'Draft':
                return t.draft;
            case 'Processed':
                return t.processed;
            case 'Posted':
                return t.posted;
            case 'Cancelled':
                return t.cancelled;
            default:
                return status;
        }
    };

    const getTotalStats = () => {
        const total = payrolls.length;
        const totalAmount = payrolls.reduce(
            (sum, p) => sum + p.totalNetSalary,
            0
        );
        const processedCount = payrolls.filter(
            (p) => p.status === 'Processed' || p.status === 'Posted'
        ).length;

        return { total, totalAmount, processedCount };
    };

    const stats = getTotalStats();

    return (
        <Box
            sx={{
                p: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                minHeight: '100vh',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    mb: 3,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Typography
                    variant="h4"
                    sx={{
                        color: '#fff',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <AttachMoney />
                    {isRTL ? t.titleAr : t.title}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="contained"
                        startIcon={<AssessmentIcon />}
                        onClick={() => navigate('/payroll-report')}
                        sx={{
                            bgcolor: '#4CAF50',
                            color: '#fff',
                            '&:hover': { bgcolor: '#45a049' },
                        }}
                    >
                        {isRTL ? 'تقرير الرواتب' : 'Payroll Report'}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenProcessDialog}
                        sx={{
                            bgcolor: '#fff',
                            color: '#667eea',
                            '&:hover': { bgcolor: '#f0f0f0' },
                        }}
                    >
                        {t.processPayroll}
                    </Button>
                    {canPost && (
                        <>
                            <Button
                                variant="contained"
                                startIcon={<PostAdd />}
                                onClick={() => setJournalDialogOpen(true)}
                                sx={{
                                    bgcolor: '#4CAF50',
                                    color: '#fff',
                                    '&:hover': { bgcolor: '#45a049' },
                                }}
                            >
                                {t.createJournal}
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<DeleteIcon />}
                                onClick={() => setDeleteJournalDialogOpen(true)}
                                sx={{
                                    bgcolor: '#f44336',
                                    color: '#fff',
                                    '&:hover': { bgcolor: '#d32f2f' },
                                }}
                            >
                                {t.deleteJournal}
                            </Button>
                        </>
                    )}
                    <IconButton
                        onClick={loadPayrolls}
                        sx={{
                            bgcolor: '#fff',
                            '&:hover': { bgcolor: '#f0f0f0' },
                        }}
                    >
                        <RefreshIcon sx={{ color: '#667eea' }} />
                    </IconButton>
                </Box>
            </Box>

            {/* Stats Cards */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 300px' }}>
                    <Card
                        sx={{
                            background:
                                'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: '#fff',
                        }}
                    >
                        <CardContent>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Box>
                                    <Typography variant="h6">
                                        {t.totalPayrolls}
                                    </Typography>
                                    <Typography
                                        variant="h3"
                                        sx={{ fontWeight: 'bold' }}
                                    >
                                        {stats.total}
                                    </Typography>
                                </Box>
                                <CalendarMonth
                                    sx={{ fontSize: 60, opacity: 0.5 }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
                <Box sx={{ flex: '1 1 300px' }}>
                    <Card
                        sx={{
                            background:
                                'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            color: '#fff',
                        }}
                    >
                        <CardContent>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Box>
                                    <Typography variant="h6">
                                        {t.totalAmount}
                                    </Typography>
                                    <Typography
                                        variant="h3"
                                        sx={{ fontWeight: 'bold' }}
                                    >
                                        {stats.totalAmount.toLocaleString(
                                            'en-GB',
                                            {
                                                minimumFractionDigits: 3,
                                                maximumFractionDigits: 3,
                                            }
                                        )}
                                    </Typography>
                                    <Typography variant="caption">
                                        {t.kwd}
                                    </Typography>
                                </Box>
                                <AttachMoney
                                    sx={{ fontSize: 60, opacity: 0.5 }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
                <Box sx={{ flex: '1 1 300px' }}>
                    <Card
                        sx={{
                            background:
                                'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                            color: '#fff',
                        }}
                    >
                        <CardContent>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Box>
                                    <Typography variant="h6">
                                        {t.processedPayrolls}
                                    </Typography>
                                    <Typography
                                        variant="h3"
                                        sx={{ fontWeight: 'bold' }}
                                    >
                                        {stats.processedCount}
                                    </Typography>
                                </Box>
                                <CheckCircle
                                    sx={{ fontSize: 60, opacity: 0.5 }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: '1 1 200px' }}>
                        <TextField
                            fullWidth
                            select
                            label={t.type}
                            value={selectedType}
                            onChange={(e) =>
                                setSelectedType(e.target.value as any)
                            }
                        >
                            <MenuItem value="">{t.allTypes}</MenuItem>
                            <MenuItem value="Management">
                                {t.management}
                            </MenuItem>
                            <MenuItem value="Branch">{t.branchType}</MenuItem>
                        </TextField>
                    </Box>
                </Box>
            </Paper>

            {/* Table */}
            <TableContainer component={Paper}>
                {loading ? (
                    <Box
                        sx={{ display: 'flex', justifyContent: 'center', p: 4 }}
                    >
                        <CircularProgress />
                    </Box>
                ) : payrolls.length === 0 ? (
                    <Box sx={{ textAlign: 'center', p: 4 }}>
                        <Typography variant="h6" color="textSecondary">
                            {t.noData}
                        </Typography>
                    </Box>
                ) : (
                    <Table>
                        <TableHead
                            sx={{
                                bgcolor:
                                    mode === 'dark' ? '#1E3A5F' : '#E3F2FD',
                            }}
                        >
                            <TableRow>
                                <TableCell
                                    sx={{
                                        color:
                                            mode === 'dark' ? '#fff' : '#000',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {t.payrollNumber}
                                </TableCell>
                                <TableCell
                                    sx={{
                                        color:
                                            mode === 'dark' ? '#fff' : '#000',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {t.type}
                                </TableCell>
                                <TableCell
                                    sx={{
                                        color:
                                            mode === 'dark' ? '#fff' : '#000',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {t.branch}
                                </TableCell>
                                <TableCell
                                    sx={{
                                        color:
                                            mode === 'dark' ? '#fff' : '#000',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {t.period}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        color:
                                            mode === 'dark' ? '#fff' : '#000',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {t.basicSalary}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        color:
                                            mode === 'dark' ? '#fff' : '#000',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {t.overtime}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        color:
                                            mode === 'dark' ? '#fff' : '#000',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {t.allowances}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        color:
                                            mode === 'dark' ? '#fff' : '#000',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {t.deductions}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        color:
                                            mode === 'dark' ? '#fff' : '#000',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {t.netSalary}
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        color:
                                            mode === 'dark' ? '#fff' : '#000',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {t.status}
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        color:
                                            mode === 'dark' ? '#fff' : '#000',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {t.actions}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {payrolls.map((payroll, index) => {
                                const monthName =
                                    months.find(
                                        (m) => m.value === payroll.month
                                    )?.label || payroll.month.toString();
                                return (
                                    <TableRow
                                        key={payroll.payrollId}
                                        hover
                                        sx={{
                                            bgcolor:
                                                index % 2 === 0
                                                    ? mode === 'dark'
                                                        ? '#263238'
                                                        : '#FFFFFF'
                                                    : mode === 'dark'
                                                      ? '#1E3A5F'
                                                      : '#F5F5F5',
                                            '&:hover': {
                                                bgcolor:
                                                    mode === 'dark'
                                                        ? '#37474F'
                                                        : '#E3F2FD',
                                            },
                                        }}
                                    >
                                        <TableCell>
                                            {payroll.payrollNumber}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={
                                                    payroll.type ===
                                                    'Management'
                                                        ? t.management
                                                        : t.branchType
                                                }
                                                size="small"
                                                color={
                                                    payroll.type ===
                                                    'Management'
                                                        ? 'primary'
                                                        : 'secondary'
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {payroll.branchName
                                                ? isRTL
                                                    ? payroll.branchNameAr
                                                    : payroll.branchName
                                                : '-'}
                                        </TableCell>
                                        <TableCell>{`${monthName} ${payroll.year}`}</TableCell>
                                        <TableCell align="right">
                                            {payroll.totalBasicSalary.toLocaleString(
                                                'en-GB',
                                                { minimumFractionDigits: 3 }
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {payroll.totalOvertimePay.toLocaleString(
                                                'en-GB',
                                                { minimumFractionDigits: 3 }
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {payroll.totalAllowances.toLocaleString(
                                                'en-GB',
                                                { minimumFractionDigits: 3 }
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            {payroll.totalDeductions.toLocaleString(
                                                'en-GB',
                                                { minimumFractionDigits: 3 }
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <strong>
                                                {payroll.totalNetSalary.toLocaleString(
                                                    'en-GB',
                                                    { minimumFractionDigits: 3 }
                                                )}
                                            </strong>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={getStatusLabel(
                                                    payroll.status
                                                )}
                                                color={
                                                    getStatusColor(
                                                        payroll.status
                                                    ) as any
                                                }
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title={t.viewDetails}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        handleViewDetails(
                                                            payroll
                                                        )
                                                    }
                                                    color="info"
                                                >
                                                    <ViewIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={isRTL ? 'طباعة' : 'Print'}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        handlePrintPayroll(
                                                            payroll
                                                        )
                                                    }
                                                    color="default"
                                                >
                                                    <PrintIcon />
                                                </IconButton>
                                            </Tooltip>
                                            {payroll.status === 'Processed' && canPost && (
                                                <>
                                                    <Tooltip title={t.post}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                handlePostPayroll(
                                                                    payroll.payrollId
                                                                )
                                                            }
                                                            color="success"
                                                            disabled={postingId === payroll.payrollId}
                                                        >
                                                            {postingId === payroll.payrollId
                                                                ? <CircularProgress size={16} color="inherit" />
                                                                : <PostAdd />}
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip
                                                        title={t.exportBankFile}
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                handleExportBankFile(
                                                                    payroll
                                                                )
                                                            }
                                                            color="primary"
                                                        >
                                                            <CloudDownload />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                            {payroll.status === 'Draft' && canEditPayroll(payroll) && (
                                                <Tooltip title={t.delete}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            handleOpenDeleteConfirm(
                                                                payroll.payrollId
                                                            )
                                                        }
                                                        color="error"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            {/* Process Dialog */}
            <Dialog
                open={processDialogOpen}
                onClose={() => setProcessDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t.processPayroll}</DialogTitle>
                <DialogContent>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            mt: 2,
                        }}
                    >
                        <TextField
                            fullWidth
                            select
                            label={t.type}
                            value={processFormData.type}
                            onChange={(e) => {
                                setProcessFormData({
                                    ...processFormData,
                                    type: e.target.value as any,
                                    branchId: undefined,
                                });
                                setProcessBranchIds([]);
                            }}
                        >
                            <MenuItem value="Management">
                                {t.management}
                            </MenuItem>
                            <MenuItem value="Branch">{t.branchType}</MenuItem>
                        </TextField>
                        {processFormData.type === 'Branch' && (
                            <Autocomplete
                                multiple
                                limitTags={2}
                                options={branches}
                                getOptionLabel={(b) => isRTL ? b.branchNameAr : b.branchName}
                                value={branches.filter(b => processBranchIds.includes(b.branchId))}
                                onChange={(_, v) => setProcessBranchIds(v.map(b => b.branchId))}
                                isOptionEqualToValue={(o, v) => o.branchId === v.branchId}
                                renderInput={(params) => (
                                    <TextField {...params} label={t.branch} placeholder={t.selectBranch} />
                                )}
                            />
                        )}

                        <Box>
                            <FormLabel component="legend">
                                {isRTL ? 'نوع الفترة' : 'Period Type'}
                            </FormLabel>
                            {/* 🔒 Fixed by the system — no manual choice (prevents overlapping periods & timesheet locks) */}
                            <Box sx={{ mt: 0.5, p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'info.main', bgcolor: 'rgba(33,150,243,0.08)' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    🔒 {periodTypeFor(processFormData.type) === 'full'
                                        ? (isRTL ? 'رواتب الإدارة: شهر كامل (من 1 إلى آخر الشهر)' : 'Management payroll: full calendar month (1st to last day)')
                                        : (isRTL ? 'رواتب الفروع: من 21 إلى 20' : 'Branch payroll: 21st to 20th')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {isRTL
                                        ? 'الفترة يحددها النظام تلقائياً حسب نوع الرواتب ولا يمكن تغييرها، حتى لا تتداخل الفترات ولا يُصرف راتب مرتين.'
                                        : 'The period is set automatically by payroll type and cannot be changed, so periods never overlap and no salary is paid twice.'}
                                </Typography>
                            </Box>
                        </Box>

                        <TextField
                            fullWidth
                            select
                            label={t.month}
                            value={processFormData.month}
                            onChange={(e) =>
                                setProcessFormData({
                                    ...processFormData,
                                    month: Number(e.target.value),
                                })
                            }
                            helperText={
                                periodTypeFor(processFormData.type) === '21-20'
                                    ? isRTL
                                        ? 'الشهر الذي ينتهي فيه (يوم 20)'
                                        : 'Month ending on 20th'
                                    : isRTL
                                      ? 'الشهر الكامل'
                                      : 'Full month'
                            }
                        >
                            {months.map((month) => (
                                <MenuItem key={month.value} value={month.value}>
                                    {month.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            fullWidth
                            type="number"
                            label={t.year}
                            value={processFormData.year}
                            onChange={(e) =>
                                setProcessFormData({
                                    ...processFormData,
                                    year: Number(e.target.value),
                                })
                            }
                            inputProps={{ min: 2020, max: 2100 }}
                        />
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label={t.notes}
                            value={processFormData.notes}
                            onChange={(e) =>
                                setProcessFormData({
                                    ...processFormData,
                                    notes: e.target.value,
                                })
                            }
                        />

                        {/* Display calculated dates */}
                        {(() => {
                            const periodType = periodTypeFor(processFormData.type);
                            const month = processFormData.month;
                            const year = processFormData.year;

                            let fromDate: Date;
                            let toDate: Date;

                            if (periodType === '21-20') {
                                fromDate = new Date(year, month - 2, 21);
                                toDate = new Date(year, month - 1, 20);
                            } else {
                                fromDate = new Date(year, month - 1, 1);
                                toDate = new Date(year, month, 0);
                            }

                            return (
                                <Box
                                    sx={{
                                        p: 2,
                                        bgcolor: 'background.default',
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: 'bold', mb: 1 }}
                                    >
                                        {isRTL
                                            ? 'الفترة المحددة:'
                                            : 'Selected Period:'}
                                    </Typography>
                                    <Typography variant="body2">
                                        {isRTL ? 'من:' : 'From:'}{' '}
                                        {fromDate.toLocaleDateString(
                                            isRTL ? 'ar-KW' : 'en-GB'
                                        )}
                                    </Typography>
                                    <Typography variant="body2">
                                        {isRTL ? 'إلى:' : 'To:'}{' '}
                                        {toDate.toLocaleDateString(
                                            isRTL ? 'ar-KW' : 'en-GB'
                                        )}
                                    </Typography>
                                    <Box
                                        sx={{
                                            mt: 1.5,
                                            p: 1,
                                            bgcolor: 'primary.main',
                                            color: 'white',
                                            borderRadius: 1,
                                            textAlign: 'center',
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 'bold' }}
                                        >
                                            {isRTL
                                                ? 'رواتب شهر'
                                                : 'Payroll for'}{' '}
                                            {toDate.toLocaleDateString(
                                                isRTL ? 'ar-KW' : 'en-GB',
                                                {
                                                    month: 'long',
                                                    year: 'numeric',
                                                }
                                            )}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })()}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setProcessDialogOpen(false)}>
                        {t.cancel}
                    </Button>
                    <Button onClick={handleProcessPayroll} variant="contained" disabled={processingPayroll}>
                        {processingPayroll ? '...' : t.process}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirm Dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
            >
                <DialogTitle>{t.delete}</DialogTitle>
                <DialogContent>
                    <Typography>{t.confirmDelete}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)}>
                        {t.cancel}
                    </Button>
                    <Button
                        onClick={handleDelete}
                        color="error"
                        variant="contained"
                    >
                        {t.delete}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Details Dialog */}
            {selectedPayroll && (
                <PayrollDetailsDialog
                    open={detailsDialogOpen}
                    onClose={() => setDetailsDialogOpen(false)}
                    payroll={selectedPayroll}
                />
            )}

            {/* Bank File Export Dialog */}
            {selectedPayroll && (
                <BankFileExportDialog
                    open={bankFileDialogOpen}
                    onClose={() => setBankFileDialogOpen(false)}
                    payrollId={selectedPayroll.payrollId}
                />
            )}

            {/* Create Journal Dialog */}
            <Dialog
                open={journalDialogOpen}
                onClose={() => setJournalDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t.createJournal}</DialogTitle>
                <DialogContent>
                    <Box
                        sx={{
                            pt: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                        }}
                    >
                        <TextField
                            select
                            label={t.selectMonth}
                            value={selectedMonth}
                            onChange={(e) =>
                                setSelectedMonth(Number(e.target.value))
                            }
                            fullWidth
                        >
                            {months.map((m) => (
                                <MenuItem key={m.value} value={m.value}>
                                    {m.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label={t.selectYear}
                            value={selectedYear}
                            onChange={(e) =>
                                setSelectedYear(Number(e.target.value))
                            }
                            fullWidth
                        >
                            {[2024, 2025, 2026, 2027].map((y) => (
                                <MenuItem key={y} value={y}>
                                    {y}
                                </MenuItem>
                            ))}
                        </TextField>
                        <Autocomplete
                            multiple
                            limitTags={2}
                            options={branches}
                            getOptionLabel={(b) => isRTL ? b.branchNameAr : b.branchName}
                            value={branches.filter(b => journalBranchIds.includes(b.branchId))}
                            onChange={(_, v) => setJournalBranchIds(v.map(b => b.branchId))}
                            isOptionEqualToValue={(o, v) => o.branchId === v.branchId}
                            renderInput={(params) => (
                                <TextField {...params} label={t.selectBranches} placeholder={t.allUnposted} />
                            )}
                        />
                        <Typography variant="caption" color="text.secondary">
                            {isRTL ? 'اتركه فارغاً لإنشاء قيد موحد لكل الفروع غير المرحّلة' : 'Leave empty to create consolidated entry for all unposted branches'}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setJournalDialogOpen(false); setJournalBranchIds([]); }}>
                        {t.cancel}
                    </Button>
                    <Button
                        onClick={handleCreateJournal}
                        variant="contained"
                        color="primary"
                    >
                        {t.createJournal}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Journal Entry Dialog */}
            <Dialog
                open={deleteJournalDialogOpen}
                onClose={() => setDeleteJournalDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t.deleteJournal}</DialogTitle>
                <DialogContent>
                    <Box
                        sx={{
                            pt: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                        }}
                    >
                        <TextField
                            select
                            label={t.selectMonth}
                            value={deleteJournalMonth}
                            onChange={(e) =>
                                setDeleteJournalMonth(Number(e.target.value))
                            }
                            fullWidth
                        >
                            {months.map((m) => (
                                <MenuItem key={m.value} value={m.value}>
                                    {m.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label={t.selectYear}
                            value={deleteJournalYear}
                            onChange={(e) =>
                                setDeleteJournalYear(Number(e.target.value))
                            }
                            fullWidth
                        >
                            {[2024, 2025, 2026].map((y) => (
                                <MenuItem key={y} value={y}>
                                    {y}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteJournalDialogOpen(false)}>
                        {t.cancel}
                    </Button>
                    <Button
                        onClick={handleDeleteJournal}
                        variant="contained"
                        color="error"
                    >
                        {t.delete}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
export default PayrollsPage;